import * as React from 'react';
import { Fragment, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { ButtonComponent, CheckBoxComponent } from '@syncfusion/ej2-react-buttons';
import { TimePickerComponent } from '@syncfusion/ej2-react-calendars';
import { DropDownListComponent, MultiSelectComponent, CheckBoxSelection } from '@syncfusion/ej2-react-dropdowns';
import { UploaderComponent } from '@syncfusion/ej2-react-inputs';
import { ToolbarComponent, ItemsDirective, ItemDirective, ContextMenuComponent, AppBarComponent } from '@syncfusion/ej2-react-navigations';
import { ScheduleComponent, Day, Week, WorkWeek, Month, Year, TimelineViews, TimelineMonth, TimelineYear, ViewsDirective, ViewDirective, ResourcesDirective, ResourceDirective, Inject, Resize, DragAndDrop, Agenda, Print, ExcelExport, ICalendarImport, ICalendarExport, Timezone } from '@syncfusion/ej2-react-schedule';
import { DropDownButtonComponent } from '@syncfusion/ej2-react-splitbuttons';
import { addClass, Browser, closest, extend, Internationalization, isNullOrUndefined, removeClass, remove, createElement, compile } from '@syncfusion/ej2-base';
import { DataManager, Predicate, Query } from '@syncfusion/ej2-data';
import { DropDownList } from '@syncfusion/ej2-dropdowns';

import { tz } from 'moment-timezone';
import "../../css/syncfusion.css";

import { L10n, loadCldr } from '@syncfusion/ej2-base';
import * as numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import * as gregorian from 'cldr-data/main/fr/ca-gregorian.json';
import * as numbers from 'cldr-data/main/fr/numbers.json';
import * as timeZoneNames from 'cldr-data/main/fr/timeZoneNames.json';

import config from '../../config/config';
import authHeader from '../../config/authHeader';

import useUsers from "../../hooks/useUsers";

loadCldr(numberingSystems, gregorian, numbers, timeZoneNames);

const now = new Date();
const todayAt = (h, m = 0) => new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0);

const ResourceHeader = (props) => {
    const name = props.resourceData?.Name ?? '';
    const role = props.resourceData?.Role ?? '';
    const color = props.resourceData?.Color ?? '#999';
    const initials = useMemo(() => name.split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase(), [name]);

    return (
        <div className="flex items-center gap-2 py-1">
            <div
                title={name}
                style={{ background: color }}
                className="flex justify-center items-center rounded-full w-8 h-8 font-semibold text-white text-sm"
            >
                {initials}
            </div>
            <div className="leading-tight">
                <div className="font-medium">{name}</div>
                <div className="opacity-70 text-[11px]">{role}</div>
            </div>
        </div>
    );
};

function AgendaTech({ users, events, sites, declarations, onEventsChanged, isPersonal }) {//{ users, events }
    const BASE_DEFAULT_VIEW = 'WorkWeek'; // Day | Week | WorkWeek | Month | Year | Agenda

    const [baseView, setBaseView] = useState(BASE_DEFAULT_VIEW);

    const resolvedView = useMemo(() => {
        if (isPersonal) return baseView;
        if (baseView === 'Agenda') return 'Agenda';
        return 'Timeline' + baseView;
    }, [baseView, isPersonal]);


    const [slotDuration, setSlotDuration] = useState(60);


    const [showCAOnly, setShowCAOnly] = useState(true);
    const [selectedIds, setSelectedIds] = useState([]);

    const caFilterInitializedRef = useRef(false);
    let scheduleObj = useRef(null);
    let contextMenuObj = useRef(null);
    let timeBtn = useRef(null);
    let selectedTarget;
    let intl = new Internationalization();
    let workWeekObj = useRef(null);
    let resourceObj = useRef(null);
    const committingRef = useRef({ create: false, change: false, remove: false });

    let liveTimeInterval;

    const BLANK_ID = '-1';
    const BLANK_USER = { Name: 'À Programmer', UserUUID: BLANK_ID, Color: '#9CA3AF', Charge_Affaire: true };
    const schedulerUsers = useMemo(
        () => {
            const base = (users ?? []).map(u => ({ ...u, UserUUID: String(u.UserUUID) }));
            return isPersonal ? base : [BLANK_USER, ...base];
        },
        [users, isPersonal]
    );


    const schedulerReady = () => !!scheduleObj.current?.resources?.[0];

    useEffect(() => {
        const allIds = (schedulerUsers ?? []).map(u => String(u.UserUUID));
        if (!allIds.length) {
            setSelectedIds([]);
            return;
        }

        const filtered = selectedIds.filter(id => allIds.includes(id));
        const next = filtered.length ? filtered : allIds;

        const changed = next.length !== selectedIds.length || next.some((v, i) => v !== selectedIds[i]);
        if (changed) setSelectedIds(next);

        /*if (schedulerReady()) {
            applyResourceFilter(next);
        }*/
    }, [schedulerUsers]);

    const normalizedEvents = useMemo(
        () => (events ?? []).map(e => ({ ...e, UserUUID: String(e.UserUUID) })),
        [events]
    );

    const weekDays = [
        { text: 'Lundi', value: 1 },
        { text: 'Mardi', value: 2 },
        { text: 'Mercredi', value: 3 },
        { text: 'Jeudi', value: 4 },
        { text: 'Vendredi', value: 5 },
        { text: 'Samedi', value: 6 },
        { text: 'Dimanche', value: 7 },
    ];

    const exportItems = [
        { text: 'iCalendar', iconCss: 'e-icons e-export' },
        { text: 'Excel', iconCss: 'e-icons e-export-excel' }
    ];
    const contextMenuItems = [
        { text: 'Nouvel événement', iconCss: 'e-icons e-plus', id: 'Add' },
        { text: 'Nouvel événement récurrent', iconCss: 'e-icons e-repeat', id: 'AddRecurrence' },
        { text: "Aujourd'hui", iconCss: 'e-icons e-timeline-today', id: 'Today' },
        { text: 'Modifier', iconCss: 'e-icons e-edit', id: 'Save' },
        { text: 'Supprimer', iconCss: 'e-icons e-trash', id: 'Delete' },
        {
            text: 'Supprimer', id: 'DeleteRecurrenceEvent', iconCss: 'e-icons e-trash',
            items: [
                { text: 'Supprimer cette occurrence', id: 'DeleteOccurrence' },
                { text: 'Supprimer la série', id: 'DeleteSeries' }
            ]
        },
        {
            text: 'Modifier', id: 'EditRecurrenceEvent', iconCss: 'e-icons e-edit',
            items: [
                { text: 'Modifier cette occurrence', id: 'EditOccurrence' },
                { text: 'Modifier la série', id: 'EditSeries' }
            ]
        }
    ];
    const calendarCollections = [
        { CalendarText: 'Personnel', UserUUID: 1, CalendarColor: '#c43081' },
        { CalendarText: 'Entreprise', UserUUID: 2, CalendarColor: '#ff7f50' },
        { CalendarText: 'Anniversaire', UserUUID: 3, CalendarColor: '#AF27CD' },
        { CalendarText: 'Vacances', UserUUID: 4, CalendarColor: '#808000' }
    ];

    const pad2 = (n) => String(n).padStart(2, "0");

    const timezoneData = [
        { text: 'UTC -12:00', value: 'Etc/GMT+12' },
        { text: 'UTC -11:00', value: 'Etc/GMT+11' },
        { text: 'UTC -10:00', value: 'Etc/GMT+10' },
        { text: 'UTC -09:00', value: 'Etc/GMT+9' },
        { text: 'UTC -08:00', value: 'Etc/GMT+8' },
        { text: 'UTC -07:00', value: 'Etc/GMT+7' },
        { text: 'UTC -06:00', value: 'Etc/GMT+6' },
        { text: 'UTC -05:00', value: 'Etc/GMT+5' },
        { text: 'UTC -04:00', value: 'Etc/GMT+4' },
        { text: 'UTC -03:00', value: 'Etc/GMT+3' },
        { text: 'UTC -02:00', value: 'Etc/GMT+2' },
        { text: 'UTC -01:00', value: 'Etc/GMT+1' },
        { text: 'UTC +00:00', value: 'Etc/GMT' },
        { text: 'UTC +01:00', value: 'Etc/GMT-1' },
        { text: 'UTC +02:00', value: 'Etc/GMT-2' },
        { text: 'UTC +03:00', value: 'Etc/GMT-3' },
        { text: 'UTC +04:00', value: 'Etc/GMT-4' },
        { text: 'UTC +05:00', value: 'Etc/GMT-5' },
        { text: 'UTC +05:30', value: 'Asia/Calcutta' },
        { text: 'UTC +06:00', value: 'Etc/GMT-6' },
        { text: 'UTC +07:00', value: 'Etc/GMT-7' },
        { text: 'UTC +08:00', value: 'Etc/GMT-8' },
        { text: 'UTC +09:00', value: 'Etc/GMT-9' },
        { text: 'UTC +10:00', value: 'Etc/GMT-10' },
        { text: 'UTC +11:00', value: 'Etc/GMT-11' },
        { text: 'UTC +12:00', value: 'Etc/GMT-12' },
        { text: 'UTC +13:00', value: 'Etc/GMT-13' },
        { text: 'UTC +14:00', value: 'Etc/GMT-14' }
    ];
    const majorSlotData = [
        { Name: '1 heure', Value: 60 },
        { Name: '1h30', Value: 90 },
        { Name: '2 heures', Value: 120 },
        { Name: '2h30', Value: 150 },
        { Name: '3 heures', Value: 180 },
        { Name: '3h30', Value: 210 },
        { Name: '4 heures', Value: 240 },
        { Name: '4h30', Value: 270 },
        { Name: '5 heures', Value: 300 },
        { Name: '5h30', Value: 330 },
        { Name: '6 heures', Value: 360 },
        { Name: '6h30', Value: 390 },
        { Name: '7 heures', Value: 420 },
        { Name: '7h30', Value: 450 },
        { Name: '8 heures', Value: 480 },
        { Name: '8h30', Value: 510 },
        { Name: '9 heures', Value: 540 },
        { Name: '9h30', Value: 570 },
        { Name: '10 heures', Value: 600 },
        { Name: '10h30', Value: 630 },
        { Name: '11 heures', Value: 660 },
        { Name: '11h30', Value: 690 },
        { Name: '12 heures', Value: 720 }
    ];
    const minorSlotData = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const timeFormatData = [
        { Name: '12 heures', Value: 'HH:mm' },
        { Name: '24 heures', Value: 'HH:mm' }
    ];
    const weekNumberData = [
        { Name: 'Désactivé', Value: 'Off' },
        { Name: 'Premier jour de l\'année', Value: 'FirstDay' },
        { Name: 'Première semaine complète', Value: 'FirstFullWeek' },
        { Name: 'Première semaine de quatre jours', Value: 'FirstFourDayWeek' }
    ];
    const tooltipData = [
        { Name: 'Désactivée', Value: 'Off' },
        { Name: 'Activée', Value: 'On' }
    ];
    const importTemplateFn = (data) => {
        const template = '<div class="e-template-btn"><span class="e-icon-left e-btn-icon e-icons e-upload-1"></span>${text}</div>';
        return compile(template.trim())(data);
    };

    const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

    const updateLiveTime = () => {
        if (!scheduleObj.current) return;

        const scheduleTimezone = scheduleObj.current.timezone || 'Etc/GMT';
        const now = new Date();

        const dateParts = now.toLocaleDateString('fr-FR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            timeZone: scheduleTimezone
        }).split(' ');

        const liveDate = [
            capitalize(dateParts[0]),
            dateParts[1],
            capitalize(dateParts[2]),
            dateParts[3]
        ].join(' ');

        const liveTime = now.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: scheduleTimezone
        });

        if (timeBtn.current) {
            timeBtn.current.innerHTML = `${liveDate} - ${liveTime}`;
        }
    };

    const onImportClick = (args) => {
        scheduleObj.current.importICalendar(args.event.target.files[0]);
    };
    const onPrint = () => {
        scheduleObj.current.print();
    };
    const onExportClick = (args) => {
        if (args.item.text === 'Excel') {
            let exportDatas = [];
            let eventCollection = scheduleObj.current.getEvents();
            let resourceCollection = scheduleObj.current.getResourceCollections();
            let resourceData = resourceCollection[0].dataSource;
            for (let resource of resourceData) {
                let data = eventCollection.filter((e) => e.UserUUID === resource.UserUUID);
                exportDatas = exportDatas.concat(data);
            }
            scheduleObj.current.exportToExcel({ exportType: 'xlsx', customData: exportDatas, fields: ['Id', 'Subject', 'StartTime', 'EndTime', 'UserUUID'] });
        }
        else {
            scheduleObj.current.exportToICalendar();
        }
    };
    const getEventData = () => {
        const date = scheduleObj.current.selectedDate;
        return {
            Id: scheduleObj.current.getEventMaxID(),
            Subject: '',
            StartTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours(), 0, 0),
            EndTime: new Date(date.getFullYear(), date.getMonth(), date.getDate(), new Date().getHours() + 1, 0, 0),
            Location: '',
            Description: '',
            IsAllDay: false,
            UserUUID: 1
        };
    };
    const labelToBaseView = (label) => {
        switch (label) {
            case 'Jour':
            case 'Day':
                return 'Day';
            case 'Semaine':
            case 'Week':
                return 'Week';
            case 'Semaine de travail':
            case 'WorkWeek':
                return 'WorkWeek';
            case 'Mois':
            case 'Month':
                return 'Month';
            case 'Année':
            case 'Year':
                return 'Year';
            case 'Agenda':
                return 'Agenda';
            default:
                return BASE_DEFAULT_VIEW;
        }
    };

    const onToolbarItemClicked = (args) => {
        const label = args.item.text;

        switch (label) {
            case 'Jour':
            case 'Day':
            case 'Semaine':
            case 'Week':
            case 'Semaine de travail':
            case 'WorkWeek':
            case 'Mois':
            case 'Month':
            case 'Année':
            case 'Year':
            case 'Agenda': {
                const base = labelToBaseView(label);
                setBaseView(base);
                break;
            }
            case 'Événement':
            case 'New Event': {
                const eventData = getEventData();
                if (eventData?.StartTime && eventData?.EndTime) {
                    scheduleObj.current.openEditor(eventData, 'Add', true);
                }
                break;
            }
            case 'Événement récurrent':
            case 'New Recurring Event': {
                const recEventData = getEventData();
                if (recEventData?.StartTime && recEventData?.EndTime) {
                    scheduleObj.current.openEditor(recEventData, 'Add', true, 1);
                }
                break;
            }
            default:
                // nothing special
                break;
        }
    };

    useEffect(() => {
        if (scheduleObj.current) {
            scheduleObj.current.currentView = resolvedView;
        }
    }, [resolvedView]);

    const isWorkCell = (el) =>
        !!el && el.classList && el.classList.contains('e-work-cells');

    const safeOpenAddEditorFromCell = (cellEl) => {
        if (!scheduleObj.current || !isWorkCell(cellEl)) return;
        const details = scheduleObj.current.getCellDetails([cellEl]); // retourne { startTime, endTime, ... }
        if (!details || !details.startTime || !details.endTime) return;
        scheduleObj.current.openEditor(details, 'Add'); // ⚠️ pas de 3e arg ici (cellDetails)
    };

    const onCellClick = (args) => {
        // args.element peut être null si on clique hors cellule
        if (!args || !args.element) return;
        // ex: simple clic -> on ne force pas l’éditeur, on ne fait rien
        // (garde si tu veux ouvrir sur simple clic)
        // safeOpenAddEditorFromCell(args.element);
    };

    const onCellDoubleClick = (args) => {
        if (!args || !args.element) return;
        safeOpenAddEditorFromCell(args.element);
    };

    const onEventClick = (args) => {
        if (!scheduleObj.current) return;
        scheduleObj.current.openEditor(args.event, 'Save');
    };
    const toDMY = d => {
        const dd = String(d.getDate()).padStart(2, '0');
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
    };


    const pad = n => String(n).padStart(2, '0');
    const getLocalTime = d => `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    const onActionBegin = async (args) => {
        if (args.requestType === 'eventChange' && committingRef.current.change) {
            committingRef.current.change = false;
            return;
        }
        if (args.requestType === 'eventCreate' && committingRef.current.create) {
            committingRef.current.create = false;
            return;
        }
        if (args.requestType === 'eventRemove' && committingRef.current.remove) {
            committingRef.current.remove = false;
            return;
        }

        if (args.requestType === 'eventChange') {
            const data = Array.isArray(args.data) ? args.data[0] : args.data;
            args.cancel = true;

            try {
                const fd = new FormData();
                fd.append('uuid', data.Id);
                fd.append('date_debut', toDMY(data.StartTime));
                fd.append('date_fin', toDMY(data.EndTime));
                fd.append('heure_debut', getLocalTime(data.StartTime));
                fd.append('heure_fin', getLocalTime(data.EndTime));
                fd.append('titre', data.Subject);
                fd.append('userUUID', data.UserUUID);
                fd.append('siteUUID', data.siteUUID);
                fd.append('declarationUUID', data.declarationUUID);

                const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_modifEvenement`, { method: 'PUT', headers: authHeader(), body: fd });
                const j = await r.json();
                if (j.status === 'success') {
                    committingRef.current.change = true;
                    scheduleObj.current?.saveEvent({ ...data }, 'Save');
                    onEventsChanged?.();
                }
            } catch { }
        }

        if (args.requestType === 'eventCreate') {
            const data = Array.isArray(args.data) ? args.data[0] : args.data;
            args.cancel = true;

            try {
                const fd = new FormData();
                fd.append('date_debut', toDMY(data.StartTime));
                fd.append('date_fin', toDMY(data.EndTime));
                fd.append('heure_debut', getLocalTime(data.StartTime));
                fd.append('heure_fin', getLocalTime(data.EndTime));
                fd.append('titre', data.Subject);
                fd.append('userUUID', data.UserUUID);
                fd.append('siteUUID', data.siteUUID);
                fd.append('declarationUUID', data.declarationUUID);

                const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_createEvenement`, { method: 'POST', headers: authHeader(), body: fd });
                const j = await r.json();
                if (j.status === 'success') {
                    committingRef.current.create = true;
                    scheduleObj.current?.addEvent({
                        ...data,
                        Id: j.uuid ?? data.Id
                    });
                    onEventsChanged?.();
                }
            } catch {/* no add */ }
        }

        if (args.requestType === 'eventRemove') {
            const data = Array.isArray(args.data) ? args.data[0] : args.data;
            args.cancel = true;

            try {
                const fd = new FormData();
                fd.append('uuid', data.Id);
                const r = await fetch(`${config.apiBaseUrl}/4DACTION/KST_deleteEvenement`, { method: 'PUT', headers: authHeader(), body: fd });
                const j = await r.json();
                if (j.status === 'success') {
                    committingRef.current.remove = true;
                    scheduleObj.current?.deleteEvent(data);
                    onEventsChanged?.();
                }
            } catch { }
        }
    };


    const normalize = (s) =>
        (s ?? '')
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLowerCase();

    const headerSiteTemplate =
        '<div class="header flex text-xs font-semibold px-2 py-1 ml-4">' +
        '<span class="columnHeader flex-1 pr-2">Nom</span>' +
        '<span class="columnHeader flex-1 pr-2">Société</span>' +
        '<span class="columnHeader flex-1">Entité</span>' +
        '</div>';

    const itemSiteTemplate =
        '<div class="flex text-xs items-center px-2 py-1">' +
        '<span class="flex-1 pr-2 truncate  text-gray-500">${Nom}</span>' +
        '<span class="flex-1 pr-2 truncate  text-gray-500">${Societe}</span>' +
        '<span class="flex-1 truncate text-gray-500">${Entite}</span>' +
        '</div>';

    const headerDeclaTemplate =
        '<div class="header flex text-xs font-semibold px-2 py-1 ml-4 ">' +
        '<span class="columnHeader flex-1 pr-2">Etat</span>' +
        '<span class="columnHeader flex-1 pr-2">Libelle</span>' +
        '<span class="columnHeader flex-1">Date Déclaration</span>' +
        '</div>';

    const itemDeclaTemplate =
        '<div class="flex text-xs items-center px-2 py-1">' +
        '<span class="flex-1 pr-2 truncate ms-ename">${Etat}</span>' +
        '<span class="flex-1 pr-2 truncate ms-job">${Libelle}</span>' +
        '<span class="flex-1 truncate ms-job">${Date_Declaration}</span>' +
        '</div>';

    const onPopupOpen = (args) => {
        console.log("args data", args.data);
        if (args.type !== 'Editor') return;

        const formElement = args.element.querySelector('.e-schedule-form');
        if (!formElement) return;

        const allDayCheckbox =
            args.element.querySelector('.e-all-day-container .e-checkbox') ||
            args.element.querySelector('.e-all-day .e-checkbox');

        if (allDayCheckbox?.ej2_instances?.[0]) {
            const chk = allDayCheckbox.ej2_instances[0];
            chk.checked = false;
            chk.dataBind?.();
        }
        args.element.querySelectorAll('.e-time-icon').forEach((icon) => {
            icon.classList.remove('e-icon-disable');
        });

        const tzContainer = args.element.querySelector('.e-time-zone-container');
        if (tzContainer) {
            tzContainer.classList.remove('e-disable');
        }

        const startObj = args.element.querySelector('.e-start')?.ej2_instances?.[0];
        const endObj = args.element.querySelector('.e-end')?.ej2_instances?.[0];

        if (startObj && endObj) {
            startObj.format = 'dd/MM/yyyy HH:mm';
            endObj.format = 'dd/MM/yyyy HH:mm';
            startObj.dataBind();
            endObj.dataBind();
        }

        if (args.element.querySelector('.custom-field-row')) return;

        const titleLocationRow = formElement.querySelector('.e-title-location-row');

        const row = createElement('div', { className: 'custom-field-row e-row' });

        const siteCol = createElement('div', { className: 'e-col-12' });
        const siteInput = createElement('input', {
            className: 'e-field',
            attrs: { name: 'siteUUID', id: 'schedule-site' }
        });
        siteCol.appendChild(siteInput);
        row.appendChild(siteCol);

        const siteDDL = new DropDownList({
            dataSource: sites ?? [],
            fields: { text: 'Nom', value: 'UUID_' },
            value: args.data?.siteUUID ?? null,
            floatLabelType: 'Always',
            placeholder: 'Site',
            allowFiltering: true,
            headerTemplate: headerSiteTemplate,
            itemTemplate: itemSiteTemplate,
            filtering: (e) => {
                const q = normalize(e.text);
                const filtered = (sites ?? []).filter(item => normalize(item.Nom).includes(q));
                e.updateData(filtered, new Query());
            }
        });
        siteDDL.appendTo(siteInput);

        const declCol = createElement('div', { className: 'e-col-12' });
        const declInput = createElement('input', {
            className: 'e-field',
            attrs: { name: 'declarationUUID', id: 'schedule-declaration' }
        });
        declCol.appendChild(declInput);
        row.appendChild(declCol);

        const declDDL = new DropDownList({
            dataSource: [],
            fields: { text: 'Libelle', value: 'UUID_' },
            value: args.data?.declarationUUID ?? null,
            floatLabelType: 'Always',
            placeholder: 'Declaration',
            headerTemplate: headerDeclaTemplate,
            itemTemplate: itemDeclaTemplate,
            enabled: false
        });
        declDDL.appendTo(declInput);

        if (titleLocationRow?.parentNode) {
            titleLocationRow.insertAdjacentElement('afterend', row);
        } else {
            formElement.firstChild.insertBefore(row, formElement.firstChild.firstChild);
        }

        const existingDeclarationUUID = args.data?.declarationUUID ?? null;

        function declarationsDataSource(siteUUID, selectedDeclarationUUID) {
            const filtered = (declarations ?? []).filter(
                d => d.UUID_Site_ === siteUUID
            );

            declDDL.dataSource = filtered;
            declDDL.dataBind();
            declDDL.enabled = !!siteUUID;

            if (selectedDeclarationUUID) {
                const exists = filtered.some(d => d.UUID_ === selectedDeclarationUUID);
                if (exists) {
                    declDDL.value = selectedDeclarationUUID;
                }
            }
        }

        siteDDL.change = () => {
            const selectedSiteUUID = siteDDL.value;
            declarationsDataSource(selectedSiteUUID, null);
        };

        const initialSiteUUID = siteDDL.value ?? null;
        if (initialSiteUUID) {
            declarationsDataSource(initialSiteUUID, existingDeclarationUUID);
        } else {
            declDDL.enabled = false;
            declDDL.value = null;
            declDDL.dataSource = [];
            declDDL.dataBind();
        }
    };
    const groupTemplate = useCallback(() => {
        return (<div className='template flex items-center'>
            <label>
                <div className='text-child'>Regroupement</div>
                <div className='icon-child'>
                    <CheckBoxComponent id='grouping' checked={true} change={(args) => { scheduleObj.current.group.resources = args.checked ? ['Users'] : []; }} />
                </div>
            </label>
        </div>);
    }, []);
    const CATemplate = useCallback(() => {
        return (<div className='template flex items-center'>
            <label>
                <div className='text-child'>Chargé d'Affaire</div>
                <div className='icon-child'>
                    <CheckBoxComponent id='ca_only' checked={showCAOnly} change={onCAFilterChange} />
                </div>

            </label>
        </div>);
    }, [showCAOnly]);
    const gridlineTemplate = useCallback(() => {
        return (<div className='template'>
            <label>
                <div className='icon-child'>
                    <CheckBoxComponent id='timeSlots' checked={true} change={(args) => { scheduleObj.current.timeScale.enable = args.checked; }} />
                </div>
                <div className='text-child'>Gride</div>
            </label>
        </div>);
    }, []);
    const autoHeightTemplate = useCallback(() => {
        return (<div className='template'>
            <label>
                <div className='icon-child'>
                    <CheckBoxComponent id='row_auto_height' checked={false} change={(args) => { scheduleObj.current.rowAutoHeight = args.checked; }} />
                </div>
                <div className='text-child'>Hauteur Auto</div>
            </label>
        </div>);
    }, []);
    const SlotDurationTemplate = useCallback(() => (
        <div className="flex flex-col items-center">
            <div className="whitespace-nowrap mr-4 text-sm">
                Durée du slot
            </div>
            <DropDownListComponent
                id="slotDuration"
                dataSource={majorSlotData}
                fields={{ text: 'Name', value: 'Value' }}
                value={slotDuration}
                popupHeight={150}
                change={(args) => {
                    const value = args.value;
                    setSlotDuration(value);

                    const sch = scheduleObj.current;
                    if (!sch) return;

                    const view = sch.currentView;
                    sch.timeScale = {
                        ...sch.timeScale,
                        interval: value,
                    };
                    sch.dataBind();
                    sch.currentView = view;
                }}
                className="min-w-[120px] mt-2"
            />
        </div>
    ), [slotDuration, majorSlotData]);


    const getDateHeaderDay = (value) => {
        return capitalize(new Intl.DateTimeFormat('fr-FR', { weekday: 'long' }).format(value));
    };

    const getDateHeaderDate = (value) => {
        return new Intl.DateTimeFormat('fr-FR', { day: 'numeric' }).format(value);
    };
    const getWeather = (value) => {
        switch (value.getDay()) {
            case 0:
                return '<img class="weather-image" src="src/css/images/sun.png" alt="Clear Weather"/>';
            case 1:
                return '<img class="weather-image" src="src/schedule/images/weather-clouds.svg" alt="Clouds Weather"/>';
            case 2:
                return '<img class="weather-image" src="src/schedule/images/weather-rain.svg" alt="Rain Weather"/>';
            case 3:
                return '<img class="weather-image" src="src/schedule/images/weather-clouds.svg" alt="Clouds Weather"/>';
            case 4:
                return '<img class="weather-image" src="src/schedule/images/weather-rain.svg" alt="Rain Weather"/>';
            case 5:
                return '<img class="weather-image" src="src/css/images/sun.png" alt="Clear Weather"/>';
            case 6:
                return '<img class="weather-image" src="src/schedule/images/weather-clouds.svg" alt="Clouds Weather"/>';
            default:
                return null;
        }
    };

    const dateHeaderTemplate = (props) => {
        return (<Fragment>
            <div>{getDateHeaderDay(props.date)}</div>
            <div>{getDateHeaderDate(props.date)}</div>
            {/*<div className="date-text" dangerouslySetInnerHTML={{ __html: getWeather(props.date) }}></div>*/}
        </Fragment>);
    };

    const applyResourceFilter = (ids) => {
        try {
            const valuesArray = isPersonal
                ? ids.map(String)                      // only real users
                : [BLANK_ID, ...ids.map(String)];      // team: keep à programmer

            const values = new Set(valuesArray);
            let predicate = null;
            values.forEach(v => {
                const p = new Predicate('UserUUID', 'equal', v);
                predicate = predicate ? predicate.or(p) : p;
            });

            const res = scheduleObj.current?.resources?.[0];
            if (res) {
                res.query = new Query().where(predicate);
            }
        } catch (e) {
            console.warn('applyResourceFilter failed:', e);
        }
    };


    const onResourceChange = (args) => {
        const selected = (args?.value ?? []).map(String);
        setSelectedIds(selected);
        if (schedulerReady()) applyResourceFilter(selected);
    };


    const onCAFilterChange = (args) => {
        setShowCAOnly(!!args.checked);

        const res = scheduleObj.current?.resources?.[0];
        console.log("scheduleobj", res);
        if (!res) return;

        if (args.checked) {
            // Show only CA users + keep the bucket user if you use it
            let predicate = new Predicate('Charge_Affaire', 'equal', true);

            // keep bucket user visible (optional)
            predicate = predicate.or(new Predicate('UserUUID', 'equal', -1));

            res.query = new Query().where(predicate);
        } else {
            // Show all users again
            res.query = new Query();
        }

        scheduleObj.current.refreshLayout();
    };

    const onDataBound = () => {
        if (isPersonal) return;
        if (!showCAOnly) return;
        if (caFilterInitializedRef.current) return; // already done once

        caFilterInitializedRef.current = true;

        const res = scheduleObj.current?.resources?.[0];
        if (!res) return;

        // Show only CA + bucket user
        let predicate = new Predicate('Charge_Affaire', 'equal', true)
            .or(new Predicate('UserUUID', 'equal', BLANK_ID));

        res.query = new Query().where(predicate);

        // This will trigger dataBound once more, but since the ref is true, it won't loop
        scheduleObj.current.refreshLayout();
    };

    const createUpload = () => {
        const element = document.querySelector('.calendar-import .e-css.e-btn');
        element.classList.add('e-inherit');
    };

    const btnClick = () => {
        let settingsPanel = document.querySelector('.overview-content .right-panel');

        if (settingsPanel?.classList.contains('hide')) {
            removeClass([settingsPanel], 'hide');
            workWeekObj.current?.refresh();
            if (!isPersonal) {
                resourceObj.current?.refresh();
            }
        } else if (settingsPanel) {
            addClass([settingsPanel], 'hide');
        }

        scheduleObj.current?.refreshEvents();
    };


    const contextMenuOpen = (args) => {
        let newEventElement = document.querySelector('.e-new-event');
        if (newEventElement) {
            remove(newEventElement);
            removeClass([document.querySelector('.e-selected-cell')], 'e-selected-cell');
        }
        scheduleObj.current.closeQuickInfoPopup();
        let targetElement = args.event.target;
        if (closest(targetElement, '.e-contextmenu')) {
            return;
        }
        selectedTarget = closest(targetElement, '.e-appointment,.e-work-cells,.e-vertical-view .e-date-header-wrap .e-all-day-cells,.e-vertical-view .e-date-header-wrap .e-header-cells');
        if (isNullOrUndefined(selectedTarget)) {
            args.cancel = true;
            return;
        }
        if (selectedTarget.classList.contains('e-appointment')) {
            let eventObj = scheduleObj.current.getEventDetails(selectedTarget);
            if (eventObj.RecurrenceRule) {
                contextMenuObj.current.showItems(['EditRecurrenceEvent', 'DeleteRecurrenceEvent'], true);
                contextMenuObj.current.hideItems(['Add', 'AddRecurrence', 'Today', 'Save', 'Delete'], true);
            }
            else {
                contextMenuObj.current.showItems(['Save', 'Delete'], true);
                contextMenuObj.current.hideItems(['Add', 'AddRecurrence', 'Today', 'EditRecurrenceEvent', 'DeleteRecurrenceEvent'], true);
            }
            return;
        }
        else if ((selectedTarget.classList.contains('e-work-cells') || selectedTarget.classList.contains('e-all-day-cells')) &&
            !selectedTarget.classList.contains('e-selected-cell')) {
            removeClass([].slice.call(scheduleObj.current.element.querySelectorAll('.e-selected-cell')), 'e-selected-cell');
            selectedTarget.setAttribute('aria-selected', 'true');
            selectedTarget.classList.add('e-selected-cell');
        }
        contextMenuObj.current.hideItems(['Save', 'Delete', 'EditRecurrenceEvent', 'DeleteRecurrenceEvent'], true);
        contextMenuObj.current.showItems(['Add', 'AddRecurrence', 'Today'], true);
    };

    const contextMenuSelect = (args) => {
        let selectedMenuItem = args.item.id;
        let eventObj = {};
        if (selectedTarget && selectedTarget.classList.contains('e-appointment')) {
            eventObj = scheduleObj.current.getEventDetails(selectedTarget);
        }
        switch (selectedMenuItem) {
            case 'Today':
                scheduleObj.current.selectedDate = new Date();
                break;
            case 'Add':
            case 'AddRecurrence':
                if (!scheduleObj.current) break;
                const selectedCells = scheduleObj.current.getSelectedElements?.() ?? [];
                const inSelection = selectedCells.some(cell => cell === selectedTarget);
                const cells = inSelection ? selectedCells : (selectedTarget ? [selectedTarget] : []);
                if (!cells.length || !isWorkCell(cells[0])) break;
                const cellDetails = scheduleObj.current.getCellDetails(cells);
                if (!cellDetails || !cellDetails.startTime || !cellDetails.endTime) break;
                if (selectedMenuItem === 'Add') {
                    scheduleObj.current.openEditor(cellDetails, 'Add');
                } else {
                    scheduleObj.current.openEditor(cellDetails, 'Add', false, 1); // récurrence
                }
                break;
            case 'Save':
            case 'EditOccurrence':
            case 'EditSeries':
                if (selectedMenuItem === 'EditSeries') {
                    let query = new Query().where(scheduleObj.current.eventFields.id, 'equal', eventObj.RecurrenceID);
                    eventObj = new DataManager(scheduleObj.current.eventsData).executeLocal(query)[0];
                }
                scheduleObj.current.openEditor(eventObj, selectedMenuItem);
                break;
            case 'Delete':
                scheduleObj.current.deleteEvent(eventObj);
                break;
            case 'DeleteOccurrence':
            case 'DeleteSeries':
                scheduleObj.current.deleteEvent(eventObj, selectedMenuItem);
                break;
        }
    };

    const timezoneChange = (args) => {
        scheduleObj.current.timezone = args.value;
        updateLiveTime();
        document.querySelector('.schedule-overview #timezoneBtn').innerHTML = args.itemData.text;
    };
    const weekNumberChange = (args) => {
        if (args.value == "Off") {
            scheduleObj.current.showWeekNumber = false;
        }
        else {
            scheduleObj.current.showWeekNumber = true;
            scheduleObj.current.weekRule = args.value;
        }
    };
    const tooltipChange = (args) => {
        if (args.value === "Off") {
            scheduleObj.current.eventSettings.enableTooltip = false;
        }
        else {
            scheduleObj.current.eventSettings.enableTooltip = true;
        }
    };
    return (<div className='schedule-control-section relative'>
        <div className='col-lg-12 control-section'>
            <div className='content-wrapper'>
                <div className='schedule-overview'>
                    <AppBarComponent colorMode="Primary">
                        <span className="time e-icons e-clock"></span>
                        <span id="timeBtn" className="time current-time" ref={timeBtn}></span>
                        <div className="e-appbar-spacer"></div>
                        <div className='control-panel calendar-export'>
                            <ButtonComponent id='printBtn' cssClass='title-bar-btn e-inherit' iconCss='e-icons e-print' onClick={(onPrint)} content='Imprimer' />
                        </div>
                        <div className='control-panel import-button'>
                            <UploaderComponent id='fileUpload' type='file' allowedExtensions='.ics' cssClass='calendar-import' buttons={{ browse: importTemplateFn({ text: 'Import' })[0] }} multiple={false} showFileList={false} selected={(onImportClick)} created={createUpload} />
                        </div>
                        <div className='control-panel calendar-export'>
                            <DropDownButtonComponent id='exportBtn' content='Exporter' cssClass='e-inherit' items={exportItems} select={onExportClick} />
                        </div>
                        <ButtonComponent id='settingsBtn' cssClass='overview-toolbar-settings e-inherit' iconCss='e-icons e-settings' iconPosition='Top' content='' onClick={btnClick} />
                    </AppBarComponent>
                    <ToolbarComponent id='toolbarOptions' cssClass='overview-toolbar' width='100%' height={70} overflowMode='Scrollable' scrollStep={100} created={() => liveTimeInterval = setInterval(() => { updateLiveTime(); }, 1000)} clicked={onToolbarItemClicked}>
                        <ItemsDirective>
                            <ItemDirective prefixIcon='e-icons e-plus' tooltipText='Nouvel événement' text='Événement' tabIndex={0} />
                            {/* <ItemDirective prefixIcon='e-icons e-repeat' tooltipText='Événement récurrent' text='Événement récurrent' tabIndex={0} />*/}
                            <ItemDirective type='Separator' />
                            <ItemDirective prefixIcon='e-icons e-day' tooltipText='Jour' text='Jour' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-week' tooltipText='Semaine' text='Semaine' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-week' tooltipText='Semaine de travail' text='Semaine de travail' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-month' tooltipText='Mois' text='Mois' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-month' tooltipText='Année' text='Année' tabIndex={0} />
                            <ItemDirective prefixIcon='e-icons e-agenda-date-range' tooltipText='Agenda' text='Agenda' tabIndex={0} />
                            <ItemDirective type='Separator' />
                            {!isPersonal && (
                                <ItemDirective
                                    tooltipText="Chargé d'affaire"
                                    text="Chargé d'affaire"
                                    template={CATemplate}
                                />
                            )}
                            <ItemDirective tooltipText='Regroupement' text='Regroupement' template={groupTemplate} />
                            {/*<ItemDirective tooltipText='Intervalles horaires' text='Intervalles horaires' template={gridlineTemplate} />
                            <ItemDirective tooltipText='Ajustement automatique des lignes' text='Hauteur automatique' template={autoHeightTemplate} />*/}
                            <ItemDirective tooltipText='Durée du slot' text='Durée du slot' template={SlotDurationTemplate} align="Right" />
                        </ItemsDirective>
                    </ToolbarComponent>
                    <div className='overview-content'>
                        <div className='left-panel'>
                            <div className='overview-scheduler'>
                                <ScheduleComponent id='scheduler' locale="fr" timeFormat="HH:mm" cssClass='schedule-overview' ref={scheduleObj} width='100%' height='100%' showQuickInfo={false} currentView={resolvedView} group={{ resources: ['Users'], orientation: 'Vertical' }} timezone="Europe/Paris"
                                    showTimeIndicator={false}
                                    dataBound={onDataBound}
                                    popupOpen={onPopupOpen}
                                    actionBegin={onActionBegin}
                                    // actionComplete={onActionComplete}
                                    eventClick={onEventClick}
                                    cellClick={onCellClick}
                                    cellDoubleClick={onCellDoubleClick}
                                    eventSettings={{
                                        dataSource: normalizedEvents ?? [],
                                        fields: {
                                            id: 'Id',
                                            subject: { name: 'Subject', title: 'Objet' },
                                            startTime: { name: 'StartTime', title: 'Début' },
                                            endTime: { name: 'EndTime', title: 'Fin' },
                                            description: { name: 'Description', title: 'Description' },
                                            resourceFields: 'UserUUID',
                                        },

                                    }} dateHeaderTemplate={dateHeaderTemplate} resourceHeaderTemplate={ResourceHeader}>
                                    <ResourcesDirective>
                                        {/* <ResourceDirective field='UserUUID' title='Calendars' name='Calendars' dataSource={calendarCollections} query={new Query().where('UserUUID', 'equal', 1)} textField='CalendarText' idField='UserUUID' colorField='CalendarColor' /> */}
                                        <ResourceDirective
                                            field="UserUUID"
                                            title="Utilisateur"
                                            name="Users"
                                            dataSource={schedulerUsers}
                                            textField="Name"
                                            idField="UserUUID"
                                            colorField="Color"
                                        />
                                    </ResourcesDirective>
                                    <ViewsDirective>
                                        <ViewDirective option='Day' />
                                        <ViewDirective option='Week' />
                                        <ViewDirective option='WorkWeek' />
                                        <ViewDirective option='Month' />
                                        <ViewDirective option='Year' />
                                        <ViewDirective option='Agenda' />
                                        <ViewDirective option='TimelineDay' />
                                        <ViewDirective option='TimelineWeek' />
                                        <ViewDirective option='TimelineWorkWeek' />
                                        <ViewDirective option='TimelineMonth' />
                                        <ViewDirective option='TimelineYear' orientation='Vertical' />
                                    </ViewsDirective>
                                    <Inject services={[Day, Week, WorkWeek, Month, Year, Agenda, TimelineViews, TimelineMonth, TimelineYear, DragAndDrop, Resize, Print, ExcelExport, ICalendarImport, ICalendarExport]} />
                                </ScheduleComponent>
                                <ContextMenuComponent id='overviewContextMenu' cssClass='schedule-context-menu' ref={contextMenuObj} target='.e-schedule' items={contextMenuItems} beforeOpen={contextMenuOpen} select={contextMenuSelect} />
                            </div>
                        </div>
                        <div className='right-panel hide'>
                            <div className='control-panel e-css'>
                                {!isPersonal && (
                                    <div className='col-row'>
                                        <div className='col-left'>
                                            <label style={{ lineHeight: '34px', margin: '0' }}>Utilisateur</label>
                                        </div>
                                        <div className='col-right'>
                                            <MultiSelectComponent id="resources" cssClass='schedule-resource' ref={resourceObj} dataSource={schedulerUsers} mode='CheckBox' fields={{ text: 'Name', value: 'UserUUID' }} enableSelectionOrder={false} showClearButton={false} showDropDownIcon={true} popupHeight={300}
                                                showSelectAll={true}
                                                value={(schedulerUsers).map(u => u.UserUUID)}
                                                selectAllText="Tout sélectionner"
                                                unSelectAllText="Tout désélectionner"
                                                change={onResourceChange}>
                                                <Inject services={[CheckBoxSelection]} />
                                            </MultiSelectComponent>
                                        </div>
                                    </div>)}

                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Premier jour de la semaine</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="weekFirstDay" dataSource={weekDays} fields={{ text: 'text', value: 'value' }} value={1} popupHeight={400} change={(args) => { scheduleObj.current.firstDayOfWeek = args.value; }} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Semaine de travail</label>
                                    </div>
                                    <div className='col-right'>
                                        <MultiSelectComponent id="workWeekDays" cssClass='schedule-workweek' ref={workWeekObj} dataSource={weekDays} mode='CheckBox' fields={{ text: 'text', value: 'value' }} enableSelectionOrder={false} showClearButton={false} showDropDownIcon={true} value={[1, 2, 3, 4, 5]} change={(args) => scheduleObj.current.workDays = args.value}>
                                            <Inject services={[CheckBoxSelection]} />
                                        </MultiSelectComponent>
                                    </div>
                                </div>
                                {/* <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Fuseau horaire</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="timezone" dataSource={timezoneData} fields={{ text: 'text', value: 'value' }} value='Etc/GMT' popupHeight={150} change={timezoneChange} />
                                    </div>
                                </div>*/}
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Heure de début de journée</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='dayStartHour' format="HH:mm" showClearButton={false} value={new Date(new Date().setHours(0, 0, 0))} change={(args) => scheduleObj.current.startHour = intl.formatDate(args.value, { skeleton: 'Hm' })} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Heure de fin de journée</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='dayEndHour' format="HH:mm" showClearButton={false} value={new Date(new Date().setHours(23, 59, 59))} change={(args) => scheduleObj.current.endHour = intl.formatDate(args.value, { skeleton: 'Hm' })} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Heure de début du travail</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='workHourStart' format="HH:mm" showClearButton={false} value={new Date(new Date().setHours(9, 0, 0))} change={(args) => scheduleObj.current.workHours.start = intl.formatDate(args.value, { skeleton: 'Hm' })} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Heure de fin du travail</label>
                                    </div>
                                    <div className='col-right'>
                                        <TimePickerComponent id='workHourEnd' format="HH:mm" showClearButton={false} value={new Date(new Date().setHours(18, 0, 0))} change={(args) => scheduleObj.current.workHours.end = intl.formatDate(args.value, { skeleton: 'Hm' })} />
                                    </div>
                                </div>
                                <div className='col-row'>
                                    <div className='col-left'>
                                        <label style={{ lineHeight: '34px', margin: '0' }}>Numérotation des semaines</label>
                                    </div>
                                    <div className='col-right'>
                                        <DropDownListComponent id="weekNumber" dataSource={weekNumberData} fields={{ text: 'Name', value: 'Value' }} value={"Off"} popupHeight={150} change={weekNumberChange} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>);
};

export default AgendaTech;